import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useState } from 'react'
import { Box, Button, Divider, LinearProgress, Paper, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import InfoIcon from '@mui/icons-material/Info'
import axios from 'axios'
import { ApiConfig } from '../../../config/api'
import { ApiResponse } from '../../../models/ApiResponse'
import { generateErrorFromResponse } from '../../../utils/errorHelper'
import { SelectedFiltersValues } from '../types/SelectedFilterValues'
import { ControlsSelector } from './ControlsSelector'
import { ButtonDownloadTemplate } from '../components/ButtonDownloadTemplate'
import {
  templateEditMassiveProcessPositions,
  templateCreateMassiveProcessPositions
} from '../../../const/templatesFileName'

export interface ControlsUploadHandle {
  upload: () => void
  isDisabled: boolean
}

interface ControlsUploadProps {
  editMode: boolean
  onClose: (success: boolean, message: string) => void
  onValidationChange?: (disabled: boolean) => void
}

export const ControlsUpload = forwardRef<ControlsUploadHandle, ControlsUploadProps>(({ editMode, onClose, onValidationChange }, ref) => {
  const initialStateFilters: SelectedFiltersValues = {
    action: editMode ? 'update' : 'create',
    warehouseId: 0,
    zoneId: 0,
    typepositionId: 0,
    activeId: -1
  }

  const [filters, setFilters] = useState<SelectedFiltersValues>(initialStateFilters)
  const maxFileSize = 2097152
  const [requireZone, setRequireZone] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [currentFile, setCurrentFile] = useState<any | undefined>(undefined)
  const [progress, setProgress] = useState<number>(0)
  const [selectedFiles, setSelectedFiles] = useState<any[] | undefined>(undefined)

  const handleSelectFile = (event: any) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const handleUpload = () => {
    const validateError = validateFile(selectedFiles)
    if (validateError !== '') {
      setCurrentFile(undefined)
      return
    }

    if (!selectedFiles) return

    const selectedFile = selectedFiles[0]
    setProgress(0)
    setCurrentFile(selectedFile)
    setIsUploading(true)

    callUpload(selectedFile, (event) => {
      setProgress(Math.round((100 * event.loaded) / event.total))
    })
      .then((response: any) => {
        const apiResponse = response.data as ApiResponse
        setIsUploading(false)
        setCurrentFile(undefined)
        setSelectedFiles(undefined)
        setProgress(0)

        if (apiResponse.success) {
          onClose(true, 'El archivo ha sido cargado para su procesamiento')
        } else {
          onClose(true, `Error en carga de archivo: ${apiResponse.message || 'falla en el servicio.'}`)
        }
      })
      .catch((error: any) => {
        setIsUploading(false)
        setProgress(0)
        setCurrentFile(undefined)
        setSelectedFiles(undefined)
        const errorMessage = generateErrorFromResponse({
          data: error.response?.data ?? error.message
        })
        onClose(true, `Error en carga de archivo: ${errorMessage}`)
      })
  }

  const validateButtonLoad = () => {
    const resultValidateControls = validateParamsToSend()
    if (!selectedFiles || isUploading || resultValidateControls) return true
    return false
  }

  const callUpload = (file: any, onUploadProgress: (event: any) => void) => {
    const formData = new FormData()
    formData.append('file', file)
    Object.keys(filters).forEach((key) => {
      formData.append(key, filters[key])
    })

    const axiosInstance = axios.create({
      baseURL: ApiConfig.ProcessApi,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return axiosInstance.post(`/PositionMassiveUploadFile`, formData, { onUploadProgress })
  }

  const sizeLabel = (size: number): string => {
    if (size / 1024 / 1024 > 1) return (size / 1024 / 1024).toFixed(2) + ' MB'
    if (size / 1024 > 1) return (size / 1024).toFixed(0) + ' KB'
    return size.toFixed(2) + ' Bytes'
  }

  const validateFile = (files: any[] | undefined): string => {
    if (!files || files.length === 0) return 'Ningún archivo seleccionado'
    const selectedFile = files[0]
    if (selectedFile.size > maxFileSize) return `El archivo seleccionado supera el tamaño máximo: ${sizeLabel(maxFileSize)}`
    if (selectedFile.name.toLowerCase().indexOf('.xls') < 0) return `El archivo seleccionado no es de tipo Excel (.xls)`
    return ''
  }

  const validateParamsToSend = () => {
    if (!editMode) {
      if (filters.warehouseId === undefined || filters.warehouseId <= 0) return true
      if (filters.zoneId === undefined || filters.zoneId <= 0) return true
      if (filters.typepositionId === undefined || filters.typepositionId <= 0) return true
      if (filters.activeId === undefined || filters.activeId < 0) return true
    }

    if (requireZone) {
      return filters.zoneId !== undefined && filters.zoneId <= 0
    }

    return false
  }

  useEffect(() => {
    onValidationChange?.(validateButtonLoad())
  }, [filters, selectedFiles, isUploading, requireZone])

  const handleChangeDDLOption = (e: any) => {
    setFilters({ ...filters, [e.target.name]: e.target.value })
  }

  useImperativeHandle(ref, () => ({
    upload: handleUpload,
    get isDisabled() { return validateButtonLoad() }
  }))

  return (
    <Paper variant='outlined' sx={{ borderRadius: 2, p: 2, height: '100%' }}>
      <Typography variant='h6' fontWeight={600} mb={0.25}>
        Seleccione la opción y complete los datos
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={1.5}>
        Para {editMode ? 'editar' : 'crear'} posiciones masivamente, primero descargue la plantilla:
      </Typography>

      <ButtonDownloadTemplate
        nameFileTemplate={editMode ? templateEditMassiveProcessPositions : templateCreateMassiveProcessPositions}
        textButtonParam={editMode ? 'Descargar Plantilla Modificación' : 'Descargar Plantilla'}
      />

      <Divider sx={{ my: 3 }} />

      <ControlsSelector
        handleChangeDDLOption={handleChangeDDLOption}
        filtersRequest={filters}
        editMode={editMode}
        isUploading={!!selectedFiles}
        setRequireZone={setRequireZone}
      />

      <Divider sx={{ my: 3 }} />

      <Grid container alignItems='center' spacing={2}>
        <Grid>
          <label htmlFor='btn-upload'>
            <input
              id='btn-upload'
              name='btn-upload'
              style={{ display: 'none' }}
              type='file'
              onChange={handleSelectFile}
              accept='.xls,.xlsx'
            />
            <Button
              variant='outlined'
              component='span'
              startIcon={<FileUploadOutlinedIcon />}
            >
              Seleccionar Archivo
            </Button>
          </label>
        </Grid>
        <Grid>
          <Typography variant='body2' color={selectedFiles ? 'text.primary' : 'text.disabled'}>
            {selectedFiles && selectedFiles.length > 0 ? selectedFiles[0].name : 'Ningún archivo seleccionado'}
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={1} mt={2} alignItems='center'>
        <Grid size='auto' display='flex' alignItems='center'>
          <InfoIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
        </Grid>
        <Grid size='grow'>
          <Typography variant='caption' color='text.secondary'>
            Primero descargue la plantilla, complétela con la información requerida, luego seleccione el archivo y finalmente haga clic en CARGAR para enviar el archivo al servidor.
          </Typography>
        </Grid>
      </Grid>

      <Fragment>
        <div style={{ margin: '10px', visibility: currentFile ? 'visible' : 'hidden' }}>
          <LinearProgress variant='determinate' value={progress} />
          <Box minWidth={35}>
            <Typography variant='body1' color='textSecondary' style={{ textAlign: 'end' }}>
              {`${progress}%`}
            </Typography>
          </Box>
        </div>
      </Fragment>
    </Paper>
  )
})
